import * as React from 'react';

import {
  Flex,
  SearchIcon,
  MenuIcon,
  CancelIcon,
  useCompassTheme,
} from '@circleci/compass';

import { LibContext } from '~/contexts/LibContext';
import { styled } from '~/styled';

import { CircleCILogo } from './CircleCILogo';
import translations from './header.json';
import { MarketingButton } from './MarketingButton';
import { MobileMenu } from './MobileMenu';
import { Navbar } from './Navbar';
import { SearchBar } from './SearchBar';
import { outerTheme } from './styles';

interface HeaderWrapperProps {
  isMobileSearchOpen: boolean;
  isMobileMenuOpen: boolean;
}

const HeaderWrapper = styled(Flex)<HeaderWrapperProps>(({
  theme,
  isMobileSearchOpen,
  isMobileMenuOpen,
}) => {
  return `
    margin: 0 auto;
    //height: ${outerTheme.globals.headerHeight}px;
    width: 100%;

    align-items: center;
    position: sticky;

    padding: ${theme.space.space16} ${theme.space.space16} ${
      isMobileSearchOpen || isMobileMenuOpen
        ? theme.space.space16
        : theme.space.space8
    } ${theme.space.space16};

    @media (min-width: ${theme.breakpoints[2]}) {
      padding: ${theme.space.space16} ${theme.space.space24} ${
        theme.space.space8
      } ${theme.space.space24};
    }
  `;
});

const HeaderSideWrapper = styled(Flex)(`
  align-items: center;
`);

const DefaultHeaderRightSide = styled(HeaderSideWrapper)(({ theme }) => {
  return `
    display: none;

    @media (min-width: ${theme.breakpoints[2]}) {
      display: flex;
    }
  `;
});

const MobileHeaderRightSide = styled(HeaderSideWrapper)(({ theme }) => {
  return `
    max-height: ${theme.space.space24};
    height: ${theme.space.space24};
    @media (min-width: ${theme.breakpoints[2]}) {
      display: none;
    }
  `;
});

const SearchWrapper = styled(Flex)<MobileMenuOpenProps>(({
  theme,
  isMobileSearchOpen,
}) => {
  return `
    display: ${isMobileSearchOpen ? 'block' : 'none'};

    @media (min-width: ${theme.breakpoints[2]}) {
      display: block;
      flex-grow: 1;
      padding: 0px 80px; // not in theme
    }
  `;
});

interface MobileMenuOpenProps {
  isMobileSearchOpen: boolean;
}

const CircleCILogoWrapper = styled.div<MobileMenuOpenProps>(
  ({ theme, isMobileSearchOpen }) => {
    return `
    display: ${isMobileSearchOpen ? 'none' : 'block'};
    margin-right: ${theme.space.space8};
  `;
  },
);

interface StyledSearchIconProps {
  onClick: () => void;
}
const StyledSearchIcon = styled(SearchIcon)<StyledSearchIconProps>(({
  theme,
}) => {
  return `
    display: inline-block;
    padding-right: ${theme.space.space8};
    cursor: pointer;
    width: ${theme.space.space32};
    transition: opacity 0.25s ease;
  `;
});

const MenuButton = styled.button(({ theme }) => {
  return `
  background: none;
  border: none;
  display: flex;
  align-items: center;
  height: 64px;
  width: 40px;

  padding-right: 0px;
  padding-left: 0px;
  justify-content: center;
  // margin-right of -8px required as justify-center is necesssary to make
  // hamburger and X in the same location when toggling menu.
  margin-right: -8px;
  cursor: pointer;
  color: ${theme.colors.n900}
`;
});

const LoginButtonWrapper = styled.div(({ theme }) => {
  return `
    padding-left: ${theme.space.space24};

    @media (max-width: ${theme.breakpoints[1]}) {
      margin: ${theme.space.space24} ${theme.space.space16};
    }
  `;
});

const VerticalRule = styled.div(({ theme }) => {
  return `
    height: 24px;
    width: 1px;
    background: ${theme.colors.n900};
  `;
});

const FullWrapper = styled(Flex)(({ theme }) => {
  return `
    width: 100%;
    box-shadow: 0 -6px 18px -3px #000000bd; // not in theme
    z-index: 100; // needs to be on top of everything (except for the TopSheet)
    background-color: ${theme.colors.white};
    position: sticky;
    top: 0px;
  `;
});

interface Props {
  activeTab?: 'home' | 'docs' | 'orbs' | 'images';
  mobileMenuCustomElement?: React.ReactNode;
}

export const Header = ({ activeTab, mobileMenuCustomElement }: Props) => {
  const { user, onSearchStateChange, internalSearchState } =
    React.useContext(LibContext);
  const locale = 'en';
  const theme = useCompassTheme();
  const desktopSize = parseInt(theme.mediaQueries.large.replace('px', ''));

  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = React.useState(
    internalSearchState.query && internalSearchState.query.length > 0
      ? true
      : false,
  );

  React.useEffect(() => {
    const resizeHandler = () => {
      // if we were in mobile view and we go to desktop view
      // close mobile menu and mobile search
      if (
        window.innerWidth >= desktopSize &&
        (isMobileMenuOpen || isMobileSearchOpen)
      ) {
        setIsMobileMenuOpen(false);
        setIsMobileSearchOpen(false);
      }

      // if we were in desktop size and were searching something
      // we want to make sure the Mobile search bar is open when resizing
      if (
        window.innerWidth < desktopSize &&
        internalSearchState.query &&
        internalSearchState.query.length > 0 &&
        !isMobileSearchOpen
      ) {
        setIsMobileSearchOpen(true);
      }
    };

    window.addEventListener('resize', resizeHandler);
    return () => {
      return window.removeEventListener('resize', resizeHandler);
    };
  }, [isMobileMenuOpen, isMobileSearchOpen, desktopSize, internalSearchState]);

  // close the search panel by removing query parameters
  const closeSearchPanel = () => {
    onSearchStateChange && onSearchStateChange({ query: null });
    setIsMobileSearchOpen(false);
  };

  return (
    <FullWrapper grow={1} direction={'column'}>
      <HeaderWrapper
        direction={'row'}
        justify={'space-between'}
        data-testid="header"
        isMobileSearchOpen={isMobileSearchOpen}
        isMobileMenuOpen={isMobileMenuOpen}
      >
        <HeaderSideWrapper direction={'row'} grow={1}>
          <CircleCILogoWrapper
            isMobileSearchOpen={isMobileSearchOpen}
            data-testid="header-circle-logo"
          >
            <CircleCILogo />
          </CircleCILogoWrapper>

          <SearchWrapper
            grow={1}
            data-testid="header-search-bar"
            isMobileSearchOpen={isMobileSearchOpen}
          >
            <SearchBar
              onCancel={() => {
                return closeSearchPanel();
              }}
            />
          </SearchWrapper>
        </HeaderSideWrapper>

        {/* Tablet + Desktop right side */}
        <DefaultHeaderRightSide
          direction={'row'}
          justify={'space-between'}
          data-testid="header-default-group"
        >
          <LoginButtonWrapper>
            <MarketingButton
              href={
                user
                  ? translations[locale].loginButton.loggedIn.href
                  : translations[locale].loginButton.loggedOut.href
              }
            >
              {user
                ? translations[locale].loginButton.loggedIn.text
                : translations[locale].loginButton.loggedOut.text}
            </MarketingButton>
          </LoginButtonWrapper>
        </DefaultHeaderRightSide>

        {/* Mobile right side */}
        <MobileHeaderRightSide
          direction={'row'}
          justify={'space-between'}
          data-testid="header-mobile-group"
        >
          {!isMobileMenuOpen && !isMobileSearchOpen && (
            <StyledSearchIcon
              data-testid="header-mobile-search-button"
              onClick={() => {
                return setIsMobileSearchOpen(!isMobileSearchOpen);
              }}
            />
          )}

          {!isMobileSearchOpen && !isMobileMenuOpen && <VerticalRule />}

          {!isMobileSearchOpen && (
            <MenuButton
              onClick={() => {
                return setIsMobileMenuOpen(!isMobileMenuOpen);
              }}
            >
              {isMobileMenuOpen ? (
                <CancelIcon data-testid="header-mobile-cancel-icon" size={30} />
              ) : (
                <MenuIcon data-testid="header-mobile-menu-icon" />
              )}
            </MenuButton>
          )}
        </MobileHeaderRightSide>
      </HeaderWrapper>
      {/* Navbar that sits at the bottom of the header */}
      {!isMobileSearchOpen && <Navbar activeTab={activeTab} />}

      {/* Mobile Menu */}
      {isMobileMenuOpen && <MobileMenu>{mobileMenuCustomElement}</MobileMenu>}
    </FullWrapper>
  );
};
